import React from 'react';
import { Button, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import DollarInput from '../../form/DollarInput';
import {
  ProductFormValues,
  applyToAllVariants,
} from '../../../utils/catalog/productForm';

interface Props {
  field: 'price' | 'cost';
  label: string;
  tooltip?: string;
}

function SingleVariantField({ field, label, tooltip }: Props) {
  const { values, setFieldValue } = useFormikContext<ProductFormValues>();
  const name = `variants.0.${field}`;
  return (
    <DollarInput
      name={name}
      label={label}
      tooltip={tooltip}
      placeholder={`Enter ${field}`}
      values={{ [name]: values.variants[0][field] }}
      setFieldValue={setFieldValue}
      disabled={false}
      hidden={false}
    />
  );
}

function AllVariantsField({ field, label }: Omit<Props, 'tooltip'>) {
  const { values, setFieldValue } = useFormikContext<ProductFormValues>();
  const [amount, setAmount] = React.useState<number | undefined>();
  const name = `${field}ForAllVariants`;
  return (
    // Stacks, not Boxes: modal.css forces MuiBox-root to 100% on mobile
    <Stack direction="row" alignItems="stretch" width="100%">
      <Stack flex={1} minWidth={0}>
        <DollarInput
          name={name}
          label={`${label} for all`}
          tooltip={`Sets every variant's ${field}; each row can still change it`}
          placeholder={`Enter ${field}`}
          values={{ [name]: amount ?? '' }}
          setFieldValue={(_, value) => setAmount(value)}
          disabled={false}
          hidden={false}
        />
      </Stack>
      <Button
        disabled={amount === undefined}
        onClick={() => {
          setFieldValue(
            'variants',
            applyToAllVariants(values.variants, { [field]: amount }),
          );
          setAmount(undefined);
        }}
        sx={{ flexShrink: 0, px: 2 }}>
        Apply to all
      </Button>
    </Stack>
  );
}

/** One variant: edits it directly. Several: writes one amount into every row on Apply. */
export default function VariantMoneyField({ field, label, tooltip }: Props) {
  const { values } = useFormikContext<ProductFormValues>();
  // separate components, so an amount typed but never applied clears when the mode changes
  return values.variants.length === 1 ? (
    <SingleVariantField field={field} label={label} tooltip={tooltip} />
  ) : (
    <AllVariantsField field={field} label={label} />
  );
}
